import { useApi } from '@src/app/context/Api';
import { ErrorHandler } from '@src/shared/ui/components/ErrorHandler';
import { useToast } from '@uhg-abyss/web/hooks/useToast';
import { dayjs } from '@uhg-abyss/web/tools/dayjs';
import { Alert } from '@uhg-abyss/web/ui/Alert';
import { Button } from '@uhg-abyss/web/ui/Button';
import { Flex } from '@uhg-abyss/web/ui/Flex';
import { Grid } from '@uhg-abyss/web/ui/Grid';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { Text } from '@uhg-abyss/web/ui/Text';
import { isEmpty, isNull, orderBy } from 'lodash';
import React, { useState } from 'react';

import { DobRecords } from './components/DobRecords';
import { SplitMerge } from './components/SplitMerge';
import { SsnRecords } from './components/SsnRecords';

/**
 * Widget: Eimp
 *
 * Displays risk records from EIMP.
 *
 * @param riskRecord
 * @returns {Element}
 * @constructor
 */
export function Eimp({ riskRecord }) {
  const eid = riskRecord?.eid;

  const trustedRecordsDob = riskRecord?.identityInformation?.eimpSourceRecords?.trustedEIMPRecords || [];
  const untrustedRecordsDob = riskRecord?.identityInformation?.eimpSourceRecords?.untrustedEIMPRecords || [];

  const trustedRecordsSsn = riskRecord?.identityInformation?.eimpSourceRecords?.trustedSsnEimpRecords || [];
  const untrustedRecordsSsn = riskRecord?.identityInformation?.eimpSourceRecords?.untrustedSsnEimpRecords || [];

  const lastUpdated = riskRecord?.identityInformation?.eimpSourceRecords?.lastRetrievedDate || '';
  const changeData = riskRecord?.identityInfo?.entityChange || {};

  const [isLoading, setIsLoading] = useState(false);

  const dateFormatted = dayjs(lastUpdated).format('ddd, MMM D, YYYY h:mm A');
  const { toast } = useToast();
  const { useApiMutation } = useApi('RiskRecord');

  const [RefreshEIMP] = useApiMutation('RefreshEIMP');

  const rowsDob = orderBy(
    [
      ...trustedRecordsDob.map((trustedRecord) => {
        return { ...trustedRecord, ...{ status: 'trusted' } };
      }),
      ...untrustedRecordsDob.map((untrustedRecord) => {
        return { ...untrustedRecord, ...{ status: 'untrusted' } };
      }),
    ],
    ['status'],
    ['asc']
  );

  const rowsSsn = orderBy(
    [
      ...trustedRecordsSsn.map((trustedRecord) => {
        return { ...trustedRecord, ...{ status: 'trusted' } };
      }),
      ...untrustedRecordsSsn.map((untrustedRecord) => {
        return { ...untrustedRecord, ...{ status: 'untrusted' } };
      }),
    ],
    ['status'],
    ['asc']
  );

  /**
   * handleRefresh
   *
   * Handle the refresh of EIMP data.
   *
   * @returns {Promise<void>}
   */
  const handleRefresh = async () => {
    const toastId = 'RefreshEIMP';

    toast.show({
      autoClose: true,
      id: `${toastId}-info`,
      isPending: true,
      message: `The EID is preparing to schedule an EIMP refresh.`,
      title: `Scheduling EIMP Refresh...`,
      variant: 'info',
    });

    setIsLoading(true);

    await RefreshEIMP(
      {
        eid,
      },
      {
        onError: () => {
          toast.hide(`${toastId}-info`);
          toast.show({
            id: `${toastId}-error`,
            message: `There was an error refreshing the EIMP.`,
            title: `Failed to Refresh EIMP`,
            variant: 'error',
          });
          setIsLoading(false);
        },
        onSuccess: () => {
          toast.hide(`${toastId}-info`);
          toast.show({
            id: `${toastId}-success`,
            message: 'Refresh times may vary',
            title: `EIMP Refresh Scheduled`,
            variant: 'success',
          });
          setIsLoading(false);
        },
      }
    );
  };

  return (
    <ErrorHandler location="src/widgets/RiskRecords/Eimp/Eimp.jsx">
      <Grid>
        <Grid.Col span={12}>
          <Flex justify="space-between">
            <Button
              ariaLoadingLabel="refreshing eimp data"
              before={<IconSymbol icon="refresh" />}
              isLoading={isLoading}
              onClick={() => {
                return handleRefresh();
              }}
            >
              Refresh Data
            </Button>
            <Text size="$sm">{`Last Updated: ${dateFormatted}`}</Text>
          </Flex>
        </Grid.Col>
        {isEmpty(rowsDob) ? (
          <Grid.Col span={{ lg: '100%', md: '100%', sm: '100%', xs: '100%' }}>
            <Alert title="There is no trusted or untrusted DOB data from EIMP for this risk record." variant="info" />
          </Grid.Col>
        ) : (
          <Grid.Col span={{ xs: '100%' }}>
            <DobRecords rows={rowsDob} />
          </Grid.Col>
        )}
        {isEmpty(rowsSsn) ? (
          <Grid.Col span={{ lg: '100%', md: '100%', sm: '100%', xs: '100%' }}>
            <Alert title="There is no trusted or untrusted SSN data from EIMP for this risk record." variant="info" />
          </Grid.Col>
        ) : (
          <Grid.Col span={{ xs: '100%' }}>
            <SsnRecords rows={rowsSsn} />
          </Grid.Col>
        )}
        {!isEmpty(changeData) && !isNull(changeData) && (
          <Grid.Col span={{ xs: '100%' }}>
            <SplitMerge changeData={changeData} />
          </Grid.Col>
        )}
      </Grid>
    </ErrorHandler>
  );
}
